// Import style
import './style.scss';

// Import animations
import { slideDown, slideUp } from '@automator/animations';

// Import utilities
import { onReady } from '@automator/utilities';

/**
 * Initializes the Accordions class when the DOM is ready.
 */
onReady( () => {
	new Accordions();
} );

/**
 * Class representing the accordions functionality.
 */
export default class Accordions {
	/**
	 * Creates an instance of Accordions.
	 */
	constructor() {
		// Create accordions
		this.createAccordions();
	}

	/**
	 * Creates accordion instances for each accordion element found in the DOM.
	 */
	createAccordions() {
		// Iterate accordions on the DOM
		[ ...this.$accordions ].forEach( ( $accordion ) => {
			// Create accordions
			new Accordion( $accordion );
		} );
	}

	/**
	 * Gets all accordion elements in the DOM.
	 *
	 * @return {NodeList} - A NodeList of accordion elements.
	 */
	get $accordions() {
		return document.querySelectorAll( '.uap-accordion' );
	}
}

/**
 * Class representing a single accordion.
 */
class Accordion {
	/**
	 * Creates an instance of Accordion.
	 *
	 * @param {HTMLElement} $accordion - The accordion element.
	 */
	constructor( $accordion ) {
		// Save the accordion element
		this.$accordion = $accordion;

		// Listen clicks to the toggles
		this.listenClicksToToggles();
	}

	/**
	 * Adds event listeners to the toggle buttons to handle click events.
	 */
	listenClicksToToggles() {
		// Iterate toggles
		[ ...this.$toggles ].forEach( ( $toggle ) => {
			// Listen clicks to the toggle
			$toggle.addEventListener( 'click', ( event ) => {
				// Get parent element
				const $item = $toggle.closest( `.${this.cssSelectors.itemClass}` );

				// Toggle item
				this.toggleItem( $item );
			} );
		} );
	}

	/**
	 * Toggles the visibility of an accordion item.
	 *
	 * @param {HTMLElement} $item - The accordion item element.
	 */
	toggleItem( $item ) {
		// Get the item elements
		const $itemElements = {
			toggle: $item.querySelector( `.${this.cssSelectors.itemToggleClass}` ),
			content: $item.querySelector( `.${this.cssSelectors.itemContentClass}` ),
		};

		// Check if the item is opened
		if ( $item.classList.contains( this.cssSelectors.itemOpenClass ) ) {
			// Close it
			slideUp( $itemElements.content, {
				duration: 300,
				onComplete: () => {
					// Remove the "open" class from the item
					$item.classList.remove( this.cssSelectors.itemOpenClass );
				},
			} );
		} else {
			// Open it

			// Add the "open" class
			$item.classList.add( this.cssSelectors.itemOpenClass );

			slideDown( $itemElements.content, {
				duration: 300,
			} );
		}
	}

	/**
	 * Gets the toggle buttons within the accordion.
	 *
	 * @return {NodeList} - A NodeList of toggle buttons.
	 */
	get $toggles() {
		return this.$accordion.querySelectorAll( `.${this.cssSelectors.itemToggleClass}` );
	}

	/**
	 * Gets the CSS selectors used for accordion items.
	 *
	 * @return {Object} - An object containing the CSS selectors for accordion items.
	 */
	get cssSelectors() {
		return {
			itemClass: 'uap-accordion-item',
			itemOpenClass: 'uap-accordion-item--open',
			itemToggleClass: 'uap-accordion-item__toggle',
			itemContentClass: 'uap-accordion-item__content',
		};
	}
}
